// The DM's screen at the table (issue 1153): a DM-only tool in the Table menu with four tabs. Screen is the DM's pinned
// tiles from the site's DM screen, each with its compact facts and opening its page on the site in the compendium's
// window; a random table rolls through the Tables tool's roll (the result in the log, private or public as the dice bag
// is set). Notes, Quests (open first, done greyed, each markable) and Relationships are the campaign's notes, plain
// text. Everything is read on open and on Refresh, over HTTP for the DM alone — never over the socket. "Edit on the
// site" opens the site's DM screen or the campaign's page, where all of it is written.
//
// qa-runs 407: a tile's facts are what its system declares for its kind, or the DM's own choice; "Facts…" on a tile
// edits that choice for every tile of its kind (which facts, in what order, up to ten), saved with the DM's DM screen on
// the site, so the site and every table of the system follow it. Clicking a tile's name opens its full card
// (`EntryCard`) with "Open on the site". The Compendium pins and unpins; the screen reads again when it does.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useShell } from '../context';
import { useDicePrivate } from '../../dicePrivacy';
import { rollTable } from '../../rolltables';
import { campaignPageUrl } from '../../npcs';
import { siteUrl } from '../../session';
import { DM_SCREEN_CHANGED, dmScreenPageUrl, markQuest, notesOf, readCampaignNotes, readDmScreen, readTileFacts, saveTileFacts, unpinTile, type CampaignNote, type DmTile, type NoteKind, type TileFactChoice } from '../../dmScreen';
import { COMPENDIUM_WINDOW } from './CompendiumPanel';
import { EntryCard } from './EntryCard';
import { BTN } from '../ui';

type Tab = 'screen' | NoteKind;
export const DM_SCREEN_TABS: ReadonlyArray<{ id: Tab; label: string }> = [
  { id: 'screen', label: 'Screen' }, { id: 'note', label: 'Notes' }, { id: 'quest', label: 'Quests' }, { id: 'relationship', label: 'Relationships' },
];
type Loaded<T> = { kind: 'loading' } | { kind: 'error'; reason: string } | ({ kind: 'ok' } & T);
const DOWN = "The DM screen can't be read just now. Try again in a moment.";
const btn = `${BTN} shrink-0`;
const EMPTY: Record<NoteKind, string> = { note: 'No notes yet.', quest: 'No quests yet.', relationship: 'No relationship notes yet.' };

export function DmScreenPanel() {
  const { campaignId } = useShell();
  const [priv] = useDicePrivate();
  const [tab, setTab] = useState<Tab>('screen');
  const [screen, setScreen] = useState<Loaded<{ system: string; tiles: DmTile[] }>>({ kind: 'loading' });
  const [notes, setNotes] = useState<Loaded<{ campaignId: string; notes: CampaignNote[] }>>({ kind: 'loading' });
  const [said, setSaid] = useState('');

  const load = useCallback(async () => {
    setScreen({ kind: 'loading' }); setNotes({ kind: 'loading' }); setSaid('');
    const [s, n] = await Promise.all([readDmScreen(campaignId).catch(() => ({ kind: 'error' as const })), readCampaignNotes(campaignId).catch(() => ({ kind: 'error' as const }))]);
    setScreen(s.kind === 'ok' ? s : { kind: 'error', reason: s.kind === 'refused' ? s.reason : DOWN });
    setNotes(n.kind === 'ok' ? n : { kind: 'error', reason: n.kind === 'refused' ? n.reason : DOWN });
  }, [campaignId]);
  useEffect(() => { void load(); }, [load]);
  // A pin or an unpin in the Compendium, or a kind's facts saved: read the screen again.
  useEffect(() => {
    const again = () => { void readDmScreen(campaignId).then((s) => { if (s.kind === 'ok') setScreen(s); }).catch(() => undefined); };
    window.addEventListener(DM_SCREEN_CHANGED, again);
    return () => window.removeEventListener(DM_SCREEN_CHANGED, again);
  }, [campaignId]);
  const unpin = async (t: DmTile) => {
    const got = await unpinTile(campaignId, t.id).catch(() => ({ kind: 'error' as const }));
    if (got.kind === 'ok') { setSaid(`${t.name ?? t.slug} is no longer on your DM screen.`); window.dispatchEvent(new Event(DM_SCREEN_CHANGED)); }
    else setSaid(got.kind === 'refused' ? got.reason : DOWN);
  };

  const roll = async (t: DmTile) => {
    const got = await rollTable(campaignId, { source: 'site', slug: t.slug }, priv);
    setSaid(got.kind === 'ok' ? `Rolled ${t.name ?? t.slug}${priv ? ', privately' : ''}: the result is in the log.` : got.kind === 'refused' ? got.reason : "That table couldn't be rolled just now.");
  };
  const mark = async (n: CampaignNote) => {
    const got = await markQuest(campaignId, n.id, n.status === 'done' ? 'open' : 'done');
    if (got.kind === 'ok') setNotes((was) => (was.kind === 'ok' ? { ...was, notes: was.notes.map((x) => (x.id === got.note.id ? got.note : x)) } : was));
    else setSaid(got.kind === 'refused' ? got.reason : DOWN);
  };

  const editUrl = tab === 'screen'
    ? (screen.kind === 'ok' ? dmScreenPageUrl(screen.system) : null)
    : (notes.kind === 'ok' ? campaignPageUrl(notes.campaignId) : null);

  return (
    <div className="flex h-full flex-col p-2 text-sm" data-testid="dm-screen">
      <div className="flex flex-wrap items-center gap-1">
        {/* Only the tabs are the tablist; Refresh and Edit sit beside it (a tablist may hold nothing but tabs). */}
        <div className="flex flex-wrap items-center gap-1" role="tablist" aria-label="DM screen">
          {DM_SCREEN_TABS.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} data-testid={`dm-screen-tab-${t.id}`}
              className={`rounded px-2 py-0.5 text-xs ${tab === t.id ? 'bg-stone-700 text-stone-100' : 'text-stone-400 hover:bg-stone-800'}`} onClick={() => setTab(t.id)}>{t.label}</button>
          ))}
        </div>
        <span className="ml-auto flex gap-1">
          <button type="button" className={btn} data-testid="dm-screen-refresh" aria-label="Refresh the DM screen" onClick={() => void load()}>Refresh</button>
          {editUrl && <a className={btn} data-testid="dm-screen-edit" href={editUrl} target={COMPENDIUM_WINDOW} rel="noopener">Edit on the site</a>}
        </span>
      </div>
      {said && <p role="status" className="mt-1 text-xs text-stone-300" data-testid="dm-screen-said">{said}</p>}
      <div className="mt-2 min-h-0 flex-1 overflow-auto" role="tabpanel">
        {tab === 'screen' ? <Tiles campaignId={campaignId} screen={screen} onRoll={roll} onUnpin={(t) => void unpin(t)} onSaid={setSaid} /> : <Notes notes={notes} kind={tab} onMark={mark} />}
      </div>
    </div>
  );
}

function Tiles({ campaignId, screen, onRoll, onUnpin, onSaid }: {
  campaignId: string; screen: Loaded<{ system: string; tiles: DmTile[] }>; onRoll: (t: DmTile) => void; onUnpin: (t: DmTile) => void; onSaid: (s: string) => void;
}) {
  const [card, setCard] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const anchors = useRef(new Map<string, HTMLElement>());
  useEffect(() => {
    if (!card) return undefined;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setCard(null); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [card]);
  if (screen.kind === 'loading') return <p role="status" data-testid="dm-screen-state" className="text-stone-400">Reading your DM screen…</p>;
  if (screen.kind === 'error') return <p role="alert" data-testid="dm-screen-state" className="text-amber-300">{screen.reason}</p>;
  if (!screen.tiles.length) {
    return (
      <p data-testid="dm-screen-state" className="text-stone-400">
        Your DM screen has nothing pinned for this game system. Pin references from the Compendium (Pin to DM screen) or on the{' '}
        <a className="underline" href={dmScreenPageUrl(screen.system)} target={COMPENDIUM_WINDOW} rel="noopener">DM screen on the site</a>.
      </p>
    );
  }
  return (
    <>
      {editing && <FactsEditor campaignId={campaignId} segment={editing} onClose={() => setEditing(null)} onSaid={onSaid} />}
      <ul className="space-y-2">
        {screen.tiles.map((t) => (
          <li key={t.id} data-testid={`dm-tile-${t.id}`} className="rounded border border-stone-800 p-2"
            ref={(el) => { if (el) anchors.current.set(t.id, el); else anchors.current.delete(t.id); }}>
            <div className="flex flex-wrap items-center gap-2">
              {t.name !== null
                ? <button type="button" className="min-w-0 flex-1 text-left font-semibold text-stone-100 underline" data-testid={`dm-tile-open-${t.id}`}
                  aria-expanded={card === t.id} aria-label={`Show ${t.name}'s card`} onClick={() => setCard((c) => (c === t.id ? null : t.id))}>{t.name}</button>
                : <span className="min-w-0 flex-1 text-stone-500">{t.slug} · no longer on the site</span>}
              {t.rollable && <button type="button" className={btn} data-testid={`dm-tile-roll-${t.id}`} aria-label={`Roll ${t.name ?? t.slug}`} onClick={() => onRoll(t)}>Roll</button>}
              {t.name !== null && <button type="button" className={btn} data-testid={`dm-tile-facts-${t.id}`} aria-label={`Choose what ${t.segment} tiles show`}
                title="Choose what this kind's tiles show" onClick={() => setEditing(t.segment)}>Facts…</button>}
            </div>
            {t.facts.length > 0 && (
              <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 text-xs" data-testid={`dm-tile-facts-list-${t.id}`}>
                {t.facts.map((f, i) => [<dt key={`${f.label}-${i}-t`} className="text-stone-500">{f.label}</dt>, <dd key={`${f.label}-${i}-d`} className="min-w-0 whitespace-pre-line break-words text-stone-300">{f.value}</dd>])}
              </dl>
            )}
            {card === t.id && t.name !== null && (
              <EntryCard campaignId={campaignId} segment={t.segment} slug={t.slug} name={t.name} family="" anchor={anchors.current.get(t.id) ?? null}
                openLabel="Open on the site" onOpen={() => { setCard(null); window.open(`${siteUrl()}${t.url}`, COMPENDIUM_WINDOW); }}
                pinned onPin={() => { setCard(null); onUnpin(t); }} onClose={() => setCard(null)} />
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * The DM's own facts for one kind's tiles (qa-runs 407): the chosen facts in order (up, down, remove), one added from the
 * kind's short attributes, at most the site's limit; Save keeps it with the DM's DM screen on the site, and "Use the
 * system's" goes back to what the game system declares. Presentation only.
 */
function FactsEditor({ campaignId, segment, onClose, onSaid }: { campaignId: string; segment: string; onClose: () => void; onSaid: (s: string) => void }) {
  const [choice, setChoice] = useState<TileFactChoice | 'loading' | { error: string }>('loading');
  const [keys, setKeys] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    setChoice('loading');
    readTileFacts(campaignId, segment).then((got) => {
      if (!live) return;
      if (got.kind === 'ok') { setChoice(got.choice); setKeys(got.choice.mine ?? got.choice.system); } else setChoice({ error: got.kind === 'refused' ? got.reason : DOWN });
    }).catch(() => { if (live) setChoice({ error: DOWN }); });
    return () => { live = false; };
  }, [campaignId, segment]);
  const save = async (facts: string[]) => {
    setBusy(true);
    const got = await saveTileFacts(campaignId, segment, facts).catch(() => ({ kind: 'error' as const }));
    setBusy(false);
    if (got.kind === 'ok') {
      onSaid(got.mine ? 'Saved: every tile of this kind shows your facts, here and on the site.' : "Back to the game system's facts for this kind.");
      window.dispatchEvent(new Event(DM_SCREEN_CHANGED));
      onClose();
    } else onSaid(got.kind === 'refused' ? got.reason : DOWN);
  };
  const move = (i: number, by: number) => setKeys((k) => { const n = [...k]; const j = i + by; if (j < 0 || j >= n.length) return k; [n[i], n[j]] = [n[j]!, n[i]!]; return n; });
  return (
    <section className="mb-2 rounded border border-stone-600 p-2" data-testid="dm-facts-editor" aria-label="What this kind's tiles show">
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 text-stone-200">{typeof choice === 'object' && 'family' in choice ? `${choice.family} tiles show` : 'Tiles show'}</p>
        <button type="button" className={btn} data-testid="dm-facts-close" onClick={onClose}>Close</button>
      </div>
      {choice === 'loading' && <p role="status" className="text-stone-400">Reading this kind's facts…</p>}
      {typeof choice === 'object' && 'error' in choice && <p role="alert" className="text-amber-300">{choice.error}</p>}
      {typeof choice === 'object' && 'family' in choice && (() => {
        const label = (k: string) => choice.available.find((a) => a.key === k)?.label ?? k;
        const rest = choice.available.filter((a) => !keys.includes(a.key));
        return (
          <>
            <ol className="mt-1 space-y-1" data-testid="dm-facts-chosen">
              {keys.map((k, i) => (
                <li key={k} className="flex items-center gap-1" data-testid={`dm-fact-${k}`}>
                  <span className="min-w-0 flex-1 text-stone-100">{label(k)}</span>
                  <button type="button" className={btn} aria-label={`Move ${label(k)} up`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                  <button type="button" className={btn} aria-label={`Move ${label(k)} down`} disabled={i === keys.length - 1} onClick={() => move(i, 1)}>↓</button>
                  <button type="button" className={btn} aria-label={`Remove ${label(k)}`} data-testid={`dm-fact-remove-${k}`} onClick={() => setKeys((ks) => ks.filter((x) => x !== k))}>✕</button>
                </li>
              ))}
            </ol>
            <div className="mt-2 flex flex-wrap items-center gap-1">
              <select aria-label="Add a fact" data-testid="dm-facts-add" className="min-w-0 flex-1 rounded bg-stone-800 px-1 py-1 text-xs" value=""
                disabled={keys.length >= choice.max || !rest.length} onChange={(e) => { const v = e.target.value; if (v) setKeys((ks) => [...ks, v]); }}>
                <option value="">{keys.length >= choice.max ? `At most ${choice.max} facts` : 'Add a fact…'}</option>
                {rest.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
              <button type="button" className={btn} data-testid="dm-facts-save" disabled={busy || !keys.length} onClick={() => void save(keys)}>Save</button>
              {choice.mine && <button type="button" className={btn} data-testid="dm-facts-reset" disabled={busy} onClick={() => void save([])}>Use the system's</button>}
            </div>
          </>
        );
      })()}
    </section>
  );
}

function Notes({ notes, kind, onMark }: { notes: Loaded<{ campaignId: string; notes: CampaignNote[] }>; kind: NoteKind; onMark: (n: CampaignNote) => void }) {
  if (notes.kind === 'loading') return <p role="status" data-testid="dm-notes-state" className="text-stone-400">Reading the campaign's notes…</p>;
  if (notes.kind === 'error') return <p role="alert" data-testid="dm-notes-state" className="text-amber-300">{notes.reason}</p>;
  const rows = notesOf(notes.notes, kind);
  if (!rows.length) return <p data-testid="dm-notes-state" className="text-stone-400">{EMPTY[kind]} Add them on the campaign's page on the site, then Refresh.</p>;
  return (
    <ul className="space-y-2" data-testid={`dm-notes-${kind}`}>
      {rows.map((n) => (
        <li key={n.id} data-testid={`dm-note-${n.id}`} data-status={n.status ?? undefined} className={`rounded border border-stone-800 p-2 ${n.status === 'done' ? 'opacity-50' : ''}`}>
          <div className="flex items-center gap-2">
            <span className={`min-w-0 flex-1 font-semibold text-stone-100 ${n.status === 'done' ? 'line-through' : ''}`}>{n.title}</span>
            {n.kind === 'quest' && (
              <button type="button" className={btn} data-testid={`dm-quest-mark-${n.id}`} onClick={() => onMark(n)}
                aria-label={n.status === 'done' ? `Mark ${n.title} open` : `Mark ${n.title} done`}>{n.status === 'done' ? 'Reopen' : 'Done'}</button>
            )}
          </div>
          {n.body && <p className="mt-1 whitespace-pre-wrap break-words text-xs text-stone-300">{n.body}</p>}
        </li>
      ))}
    </ul>
  );
}
